<script setup lang="ts">
import { UiCard, UiChip, UiText } from "@shopexperts/shared";
import type { BannedIp } from "@shopexperts/legacy-api";
import BanListActionButton from "./BanListActionButton.vue";
import BanListDetail from "./BanListDetail.vue";
import { formatBanDate, formatSource, sourceChipVariant } from "@/app/composables/ban-list/banListFormatters";

defineProps<{
    entry: BannedIp;
    isBusy?: boolean;
}>();

const emit = defineEmits<{
    (e: "remove"): void;
    (e: "filter-by-rule", ruleId: number): void;
    (e: "copy"): void;
}>();
</script>

<template>
    <UiCard padding="md" class="transition-shadow hover:shadow-md">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                        <UiText as="p" variant="h6" class="font-mono font-semibold break-all text-se-dark">
                            {{ entry.ip_address }}
                        </UiText>
                        <UiChip :text="formatSource(entry.source)" :variant="sourceChipVariant(entry.source)" size="sm" />
                        <UiChip
                            v-if="entry.hits_count > 1"
                            :text="`${entry.hits_count} sightings`"
                            variant="neutral"
                            size="sm"
                        />
                    </div>

                    <button
                        v-if="entry.ban_rule"
                        type="button"
                        class="w-fit text-left text-sm text-se-dark underline decoration-se-gray-300 underline-offset-2 hover:decoration-se-dark"
                        @click="emit('filter-by-rule', entry.ban_rule.id)"
                    >
                        Rule: {{ entry.ban_rule.name }}
                    </button>
                </div>

                <div class="flex flex-wrap items-center gap-3 xl:shrink-0">
                    <BanListActionButton label="Copy IP" icon="copy" tone="primary" :disabled="isBusy" @click="emit('copy')" />
                    <BanListActionButton label="Remove" icon="delete" tone="danger" :disabled="isBusy" @click="emit('remove')" />
                </div>
            </div>

            <div class="grid grid-cols-1 gap-3 border-t border-se-gray-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
                <BanListDetail label="Associated email" :value="entry.email" />
                <BanListDetail label="First seen" :value="formatBanDate(entry.first_seen_at, 'Unknown')" />
                <BanListDetail label="Last seen" :value="formatBanDate(entry.last_seen_at, 'Unknown')" />
                <BanListDetail label="Notes" :value="entry.notes" />
            </div>
        </div>
    </UiCard>
</template>
